import { getAxios, getWorkspaceIdFromLocalStorage, refreshWorkspaceId } from '@dataplace.ai/functions/utils'
import { Signal, useSignal } from '@preact/signals-react'
import { trafficVersions } from 'apps/placeme/src/constants/constants'
import { ENDPOINTS } from 'apps/placeme/src/constants/endpoints'
import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

export type FeatureFlagsObject = {
  pdfV2: boolean
  trafficVersion: (typeof trafficVersions)[keyof typeof trafficVersions]
  overview: {
    enabled: boolean
    llmSummary: boolean
  }
} | undefined
export interface IFeatureFlagsContext {
  featureFlags: FeatureFlagsObject
  setFeatureFlags: Dispatch<SetStateAction<FeatureFlagsObject>>
  getFeatureFlags(): void
  requesting: Signal<boolean>
}

export const FeatureFlagsContext = createContext<IFeatureFlagsContext | null>(null)

interface ProgressProviderProps {
  children: ReactNode
}

export function FeatureFlagsContextProvider({ children }: ProgressProviderProps): JSX.Element {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsObject>()
  const requesting = useSignal(false)

  const getFeatureFlags = useCallback(async () => {
    requesting.value = true

    const workspaceId = getWorkspaceIdFromLocalStorage()
    if (!workspaceId?.length) {
      requesting.value = false
      return
    }

    try {
      const axiosInstance = await getAxios()
      const { data } = await axiosInstance.get<FeatureFlagsObject>(ENDPOINTS.FEATURE_FLAGS)

      setFeatureFlags(data)
    } catch {
      setFeatureFlags(undefined)
    } finally {
      requesting.value = false
    }
  }, [getAxios, setFeatureFlags])

  useEffect(() => {
    // detect updating workspace id
    window.addEventListener(refreshWorkspaceId, getFeatureFlags)
    // remove listener
    return () => window.removeEventListener(refreshWorkspaceId, getFeatureFlags)
  }, [getFeatureFlags])

  const contextValue = useMemo(() => ({
    featureFlags,
    setFeatureFlags,
    getFeatureFlags,
    requesting,
  }), [featureFlags])

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}
